import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { ArrowDown01Icon } from '@hugeicons/react'
import clsx from 'clsx'

import styles from './AppMenuScrollArea.module.scss'

type AppMenuScrollAreaProps = {
  children: ReactNode
  /** Nome acessível da região rolável e texto do aviso de "há mais abaixo". */
  label?: string
  hintLabel?: string
  className?: string
  /**
   * Se a área estica para preencher o espaço do flex pai mesmo com conteúdo
   * curto, ou só ocupa o que o conteúdo precisa.
   *
   * `true` (padrão) mantém o menu lateral, onde a navegação empurra o cartão
   * de recompensas para o fim do drawer. Com `false` o bloco fica do tamanho
   * do conteúdo e ainda assim rola quando ele excede o espaço.
   */
  grow?: boolean
}

type ScrollThumbMetrics = {
  height: number
  top: number
  visible: boolean
}

const TRACK_INSET = 12
const THUMB_MIN_HEIGHT = 44
const OVERFLOW_THRESHOLD = 12

export function AppMenuScrollArea({
  children,
  label = 'Navegação do menu',
  hintLabel = 'Role para ver mais itens do menu',
  className,
  grow = true
}: AppMenuScrollAreaProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [hasOverflow, setHasOverflow] = useState(false)
  const [showMoreHint, setShowMoreHint] = useState(false)
  const [thumbMetrics, setThumbMetrics] = useState<ScrollThumbMetrics>({
    height: 0,
    top: 0,
    visible: false
  })

  const updateScrollState = useCallback(() => {
    const node = scrollRef.current
    if (!node) return

    const { scrollHeight, clientHeight, scrollTop } = node
    const overflow = scrollHeight - clientHeight
    const hasScrollableOverflow = overflow > OVERFLOW_THRESHOLD
    const nearBottom =
      scrollTop + clientHeight >= scrollHeight - OVERFLOW_THRESHOLD

    setHasOverflow(hasScrollableOverflow)
    setShowMoreHint(hasScrollableOverflow && !nearBottom)

    if (!hasScrollableOverflow) {
      setThumbMetrics({ height: 0, top: 0, visible: false })
      return
    }

    const trackHeight = clientHeight - TRACK_INSET * 2
    const thumbHeight = Math.max(
      THUMB_MIN_HEIGHT,
      (clientHeight / scrollHeight) * trackHeight
    )
    const maxThumbTop = Math.max(trackHeight - thumbHeight, 0)
    const scrollRatio = overflow > 0 ? scrollTop / overflow : 0

    setThumbMetrics({
      height: thumbHeight,
      top: scrollRatio * maxThumbTop,
      visible: true
    })
  }, [])

  const scrollDown = useCallback(() => {
    const node = scrollRef.current
    if (!node) return

    node.scrollBy({
      top: Math.min(160, node.clientHeight * 0.55),
      behavior: 'smooth'
    })
  }, [])

  useEffect(() => {
    const node = scrollRef.current
    if (!node) return undefined

    updateScrollState()

    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(node)

    return () => resizeObserver.disconnect()
  }, [children, updateScrollState])

  return (
    <div
      className={clsx(
        'relative flex min-h-0 flex-col',
        grow ? 'flex-1' : 'flex-initial',
        className
      )}
    >
      <div
        ref={scrollRef}
        role="region"
        aria-label={label}
        tabIndex={0}
        onScroll={updateScrollState}
        className={clsx(
          styles.scrollArea,
          // Altura por flex, não `h-full`: `height: 100%` só resolve contra pai
          // de altura *especificada*, e a daqui vem do flex. Onde a cadeia acima
          // é de altura automática, a porcentagem cai para `auto` e a área
          // cresce até o conteúdo inteiro — sem rolagem, transbordando por cima
          // do rodapé. O `grow` precisa valer nos dois níveis: com `flex-1` fixo
          // aqui, um wrapper `flex-initial` por fora não basta.
          'min-h-0 overflow-y-auto overscroll-contain pr-5 [-webkit-overflow-scrolling:touch]',
          grow ? 'flex-1' : 'flex-initial'
        )}
      >
        {children}
      </div>

      {thumbMetrics.visible ? (
        <div className={styles.scrollTrack} aria-hidden>
          <div
            className={styles.scrollThumb}
            style={{
              height: `${thumbMetrics.height}px`,
              transform: `translateY(${thumbMetrics.top}px)`
            }}
          />
        </div>
      ) : null}

      {/* Sem reservar espaço no conteúdo para este aviso: ele é sobreposto e
          some ao chegar no fim, onde a reserva viraria só vão branco. */}
      {hasOverflow ? (
        <button
          type="button"
          onClick={scrollDown}
          aria-label={hintLabel}
          aria-hidden={!showMoreHint}
          tabIndex={showMoreHint ? 0 : -1}
          className={clsx(
            styles.hint,
            // `-bottom-px`: a altura do container cai em fração de pixel, e a
            // borda do degradê arredonda um pixel acima da borda real da área
            // rolável, deixando uma fresta sem esmaecer.
            'absolute inset-x-0 -bottom-px flex h-14 items-end justify-center pb-1.5',
            !showMoreHint && styles.hintHidden
          )}
        >
          <span className={styles.hintBadge}>
            <ArrowDown01Icon
              size={12}
              variant="solid"
              className={styles.hintIcon}
              aria-hidden
            />
            Role para ver mais
          </span>
        </button>
      ) : null}
    </div>
  )
}
